import { useState } from "react";
import { Check, ArrowRight, Sparkles } from "lucide-react";

const CHOICES = [
  { key: "fait", label: "Vrai (fait historique)" },
  { key: "croyance", label: "Croyance" },
  { key: "legende", label: "Légende" },
];

// Static, instant, no-friction mini-game: distinguishing fact / belief / legend.
const ITEMS = [
  {
    statement: "Le pendule était utilisé dans l'Antiquité exactement comme aujourd'hui.",
    answer: "legende",
    explain: "Idée répandue mais trompeuse. L'usage d'un objet suspendu pour « répondre » est ancien, mais le pendule divinatoire tel qu'on le pratique aujourd'hui s'est surtout formalisé avec la radiesthésie aux XIXe–XXe siècles. On lui prête une continuité antique qui relève surtout du récit.",
  },
  {
    statement: "Catherine de Médicis s'entourait d'astrologues à la cour de France.",
    answer: "fait",
    explain: "Historiquement attesté : l'astrologie tenait une place réelle dans plusieurs cours européennes de la Renaissance. Cela dit, attester d'une pratique ne valide pas ses prétentions : c'est un fait de société, pas une preuve d'efficacité.",
  },
  {
    statement: "Tirer la carte de La Mort au tarot annonce un décès prochain.",
    answer: "croyance",
    explain: "C'est une interprétation, pas un fait. Dans la lecture symbolique la plus courante, cette lame évoque une fin, une transformation, un passage — rarement une mort littérale. Tout dépend du cadre de croyance de la personne.",
  },
  {
    statement: "Le tarot de Marseille était à l'origine un livre secret de sagesse égyptienne.",
    answer: "legende",
    explain: "Belle légende née au XVIIIe siècle (Court de Gébelin), mais sans fondement historique établi. Le tarot apparaît d'abord comme un jeu de cartes en Europe ; son usage divinatoire est plus tardif.",
  },
  {
    statement: "Au Moyen Âge, certaines « sorcières » étaient en réalité des guérisseuses populaires.",
    answer: "fait",
    explain: "Documenté : des femmes détenaient des savoirs sur les plantes et les soins. L'étiquette de « sorcellerie » a souvent recouvert des réalités sociales très diverses. Prudence toutefois : chaque cas mérite son contexte.",
  },
];

export default function TrueBeliefLegend() {
  const [i, setI] = useState(0);
  const [choice, setChoice] = useState(null);
  const item = ITEMS[i];
  const answered = choice !== null;
  const correct = answered && choice === item.answer;

  const next = () => { setChoice(null); setI((n) => (n + 1) % ITEMS.length); };

  return (
    <div data-testid="vcl-game" className="border border-[rgba(198,167,106,0.3)] bg-[#0B1020] p-8">
      <div className="flex items-center gap-2 mb-5">
        <Sparkles size={15} className="text-[#C6A76A]" />
        <div className="dl-eyebrow">Vrai, croyance ou légende ?</div>
        <span className="ml-auto text-xs text-[#F3E9D2]/40">{i + 1} / {ITEMS.length}</span>
      </div>

      <p className="serif text-2xl md:text-3xl text-[#F3E9D2] leading-snug min-h-[4.5rem]">« {item.statement} »</p>

      {!answered ? (
        <div className="flex flex-wrap gap-3 mt-6">
          {CHOICES.map((c) => (
            <button key={c.key} data-testid={`vcl-choice-${c.key}`} onClick={() => setChoice(c.key)}
              className="dl-btn" style={{ padding: "0.7rem 1.4rem", fontSize: "0.72rem" }}>
              {c.label}
            </button>
          ))}
        </div>
      ) : (
        <div className="mt-6 dl-fade">
          <div className={`inline-flex items-center gap-2 text-sm font-semibold ${correct ? "text-[#8fce9b]" : "text-[#D4B978]"}`}>
            {correct ? <><Check size={16} /> Bien vu.</> : <>La réponse : {CHOICES.find((c) => c.key === item.answer).label}.</>}
          </div>
          <p className="text-[#F3E9D2]/80 leading-relaxed mt-3 border-l-2 border-[#C6A76A] pl-4">{item.explain}</p>
          <p className="dl-eyebrow mt-3" style={{ fontSize: "0.55rem" }}>Dorothée IA</p>
          <button data-testid="vcl-next" onClick={next} className="dl-btn dl-btn-solid mt-6" style={{ padding: "0.7rem 1.5rem", fontSize: "0.72rem" }}>
            Autre affirmation <ArrowRight size={15} />
          </button>
        </div>
      )}
    </div>
  );
}
